import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import Typography from '../../typography/Typography';
import { chartColors } from '../hardware/AssetAgeChart';

interface ChartDataItem {
  name: string;
  percentage: number;
  [key: string]: any;
}

interface StackedBarData {
  data: ChartDataItem[];
  unit?: string;
  showLegend?: boolean;
  index?: number | null;
}

const StackedBarChart = ({
  data,
  unit,
  showLegend = false,
  index = null,
}: StackedBarData) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(index || null);
  const [isExpanded, setIsExpanded] = useState(false);

  const coloredData = data.map((item, index) => ({
    ...item,
    color: item.color || chartColors[index % chartColors.length],
  }));

  const totalValue = data.reduce(
    (acc, curr) => acc + (curr.percentage || 0),
    0
  );

  useEffect(() => {
    setActiveIndex(index);
  }, [index]);

  // Constants for legend visibility
  const LIMIT = 8;
  const shouldShowExpand = coloredData.length > LIMIT;
  const visibleData = isExpanded ? coloredData : coloredData.slice(0, LIMIT);

  return (
    <div className={`w-full flex flex-col gap-3 p-4`}>
      <div className="flex justify-between items-center px-0.5">
        <Typography tag="span" type="label">
          Verdeling
        </Typography>
        <Typography tag="span" type="label">
          {totalValue}U
        </Typography>
      </div>

      <div className="relative w-full h-4 flex items-center group">
        <div className="flex w-full h-full rounded-fullgap-0.5 shadow-inner">
          {coloredData.map((segment, i) => (
            <div
              key={i}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(index)}
              className="h-full transition-all duration-200 cursor-pointer relative"
              style={{
                width: `${(segment.percentage / totalValue) * 100}%`,
                backgroundColor: segment.color,
                opacity: activeIndex === i ? 1 : 0.8,
              }}
            >
              {activeIndex === i && (
                <div
                  className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-900 text-white border border-1 text-[9px] py-1 px-2 rounded shadow-lg whitespace-nowrap"
                  style={{ borderColor: segment.color }}
                >
                  <Typography tag="span" type="label">
                    {coloredData[activeIndex].name}:{' '}
                    {coloredData[activeIndex].percentage}
                    {unit}
                  </Typography>

                  <div className="absolute bottom-[-4px] left-1/2 w-2 h-2 bg-white dark:bg-gray-900 rotate-45 -translate-x-1/2"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showLegend && (
        <div className="flex flex-col gap-1.5 w-full">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {visibleData.map((item, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                className={`flex items-center gap-2 transition-opacity duration-200 cursor-pointer ${activeIndex !== null && activeIndex !== index ? 'opacity-40' : 'opacity-100'}`}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <Typography tag="span" type="label">
                  {item.name}{' '}
                  <span className="text-gray-400 ml-1">
                    {item.percentage}
                    {unit}
                  </span>
                </Typography>
              </div>
            ))}
          </div>

          {/* Compact Expand Toggle */}
          {shouldShowExpand && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors mt-1"
            >
              <Typography tag="span" type="label">
                {isExpanded
                  ? 'Collapse'
                  : `+ ${coloredData.length - LIMIT} More`}
              </Typography>
              {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
